import { forwardRef } from "react";

export const FormInput = forwardRef(({ 
  label, 
  icon: Icon, 
  required = false, 
  error,
  type,
  name,
  value,
  onChange,
  placeholder,
  disabled,
  ...restProps 
}, ref) => (
  <div>
    {label && (
      <label className="block text-sm text-[#A3A3A3] mb-2">
        {label} {required && "*"}
      </label>
    )}
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
      )}
      <input
        ref={ref}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full input-dark ${Icon ? 'pl-12' : 'pl-4'} pr-4 py-4`}
        {...restProps}
      />
    </div>
    {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
  </div>
));

FormInput.displayName = "FormInput";

export const FormSelect = ({ 
  label, 
  icon: Icon, 
  required = false, 
  children, 
  name,
  value,
  onChange,
  disabled,
  id
}) => {
  return (
    <div>
      {label && (
        <label className="block text-sm text-[#A3A3A3] mb-2">
          {label} {required && "*"}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
        )}
        <select
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`w-full input-dark ${Icon ? 'pl-12' : 'pl-4'} pr-4 py-4 appearance-none cursor-pointer`}
        >
          {children}
        </select>
      </div>
    </div>
  );
};

export const FormTextarea = ({ 
  label, 
  required = false, 
  name,
  value,
  onChange,
  placeholder,
  rows,
  disabled,
  id
}) => {
  return (
    <div>
      {label && (
        <label className="block text-sm text-[#A3A3A3] mb-2">
          {label} {required && "*"}
        </label>
      )}
      <textarea
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className="w-full input-dark px-4 py-4 min-h-[120px] resize-y"
      />
    </div>
  );
};

export const RadioGroup = ({ label, name, options, value, onChange, required = false }) => (
  <div className="space-y-3">
    {label && (
      <label className="block text-sm text-[#A3A3A3]">
        {label} {required && "*"}
      </label>
    )}
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {options.map(option => (
        <label
          key={option}
          className={`flex items-center justify-center p-4 border cursor-pointer transition-all ${
            value === option
              ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
              : 'border-white/10 hover:border-white/30 text-white'
          }`}
        >
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option}
            onChange={onChange}
            className="sr-only"
          />
          <span className="text-sm text-center">{option}</span>
        </label>
      ))}
    </div>
  </div>
);

export const CheckboxGroup = ({ label, name, options, selected, onChange, required = false, columns = 2 }) => (
  <div className="space-y-3">
    {label && (
      <label className="block text-sm text-[#A3A3A3]">
        {label} {required && "*"}
      </label>
    )}
    <div className={`grid grid-cols-1 sm:grid-cols-${columns} gap-3`}>
      {options.map(option => (
        <label
          key={option}
          className={`flex items-center gap-3 p-4 border cursor-pointer transition-all ${
            selected.includes(option)
              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
              : 'border-white/10 hover:border-white/30'
          }`}
        >
          <input
            type="checkbox"
            name={name}
            value={option}
            checked={selected.includes(option)}
            onChange={() => onChange(name, option)}
            className="sr-only"
          />
          <div className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 ${
            selected.includes(option)
              ? 'border-[#D4AF37] bg-[#D4AF37]'
              : 'border-white/30'
          }`}>
            {selected.includes(option) && (
              <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm text-white">{option}</span>
        </label>
      ))}
    </div>
  </div>
);
